import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, AuthUser } from "@/hooks/useAuth";

interface LoginFormProps {
  onSwitch: () => void;
}

const mockUsers: AuthUser[] = [
  { id: "u1", name: "Alex Chen", email: "buyer@demo.com", role: "buyer", avatar: "https://public.readdy.ai/ai/img_res/15d407882e4a0b8e38736bea6dde725a.jpg", verified: true },
  { id: "u2", name: "TechVision Factory", email: "seller@demo.com", role: "seller", avatar: "https://readdy.ai/api/search-image?query=modern%20tech%20company%20logo%20icon%20professional%20orange&width=60&height=60&seq=usr2&orientation=squarish", verified: true, storeName: "TechVision Factory" },
  { id: "u3", name: "Jessica Martinez", email: "influencer@demo.com", role: "influencer", avatar: "https://readdy.ai/api/search-image?query=young%20woman%20influencer%20portrait%20professional%20headshot%20warm%20lighting&width=60&height=60&seq=usr3&orientation=squarish", verified: true, platform: "tiktok", followers: "2.3M" },
];

type LoginMethod = "email" | "phone";

export default function LoginForm({ onSwitch }: LoginFormProps) {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [method, setMethod] = useState<LoginMethod>("email");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const sendOtp = () => {
    if (!phone || phone.length < 7) { setError("Please enter a valid phone number."); return; }
    setError("");
    setOtpLoading(true);
    setTimeout(() => { setOtpSent(true); setOtpLoading(false); }, 1000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (method === "phone") {
      if (!otp || otp.length < 4) { setError("Please enter the verification code."); return; }
      setLoading(true);
      setTimeout(() => {
        login(mockUsers[0]);
        navigate("/");
        setLoading(false);
      }, 800);
      return;
    }

    if (!email || !password) { setError("Please fill in all fields."); return; }
    setLoading(true);
    setTimeout(() => {
      const found = mockUsers.find((u) => u.email === email);
      if (found && password.length >= 4) {
        login(found);
        if (found.role === "seller") navigate("/dashboard");
        else navigate("/");
      } else {
        setError("Invalid email or password. Try: buyer@demo.com / seller@demo.com / influencer@demo.com");
      }
      setLoading(false);
    }, 800);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Method Toggle */}
      <div className="flex p-1 bg-gray-100 rounded-xl gap-1 mb-2">
        <button
          type="button"
          onClick={() => { setMethod("email"); setError(""); setOtpSent(false); }}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${method === "email" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-mail-line" /> Email
        </button>
        <button
          type="button"
          onClick={() => { setMethod("phone"); setError(""); }}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${method === "phone" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"}`}
        >
          <i className="ri-smartphone-line" /> Phone
        </button>
      </div>

      {method === "email" ? (
        <>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Email Address</label>
            <div className="relative">
              <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-sm font-semibold text-gray-700">Password</label>
              <button type="button" className="text-xs text-orange-500 font-medium hover:underline cursor-pointer">Forgot password?</button>
            </div>
            <div className="relative">
              <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type={showPass ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full pl-10 pr-11 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors"
              />
              <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Phone Number</label>
            <div className="flex gap-2">
              <div className="flex items-center gap-1.5 px-3 py-3 border border-gray-200 rounded-xl bg-gray-50 text-sm font-medium text-gray-700 flex-shrink-0">
                <span>🇺🇸</span> +1
              </div>
              <div className="relative flex-1">
                <i className="ri-smartphone-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="(555) 000-0000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors"
                />
              </div>
            </div>
          </div>

          {!otpSent ? (
            <button
              type="button"
              onClick={sendOtp}
              disabled={otpLoading}
              className="w-full py-3 border-2 border-orange-500 text-orange-500 font-bold rounded-xl hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60"
            >
              {otpLoading ? <><i className="ri-loader-4-line animate-spin mr-2" />Sending...</> : <><i className="ri-message-2-line mr-2" />Send Verification Code</>}
            </button>
          ) : (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                Verification Code
                <span className="text-xs text-green-600 font-normal ml-2"><i className="ri-checkbox-circle-fill" /> Code sent to {phone}</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-orange-400 transition-colors text-center tracking-widest font-bold text-lg"
                />
                <button type="button" onClick={sendOtp} className="px-4 py-3 text-xs text-orange-500 font-semibold border border-orange-200 rounded-xl hover:bg-orange-50 cursor-pointer whitespace-nowrap">
                  Resend
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">Demo: enter any 4+ digit code to continue</p>
            </div>
          )}
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl px-3 py-2.5 text-xs text-red-600 flex items-start gap-2">
          <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />{error}
        </div>
      )}

      <div className="bg-orange-50 border border-orange-100 rounded-xl px-3 py-2.5 text-xs text-orange-700">
        <strong>Demo:</strong> buyer@demo.com · seller@demo.com · influencer@demo.com (any password)
      </div>

      <button
        type="submit"
        disabled={loading || (method === "phone" && !otpSent)}
        className="w-full py-3.5 bg-orange-500 text-white font-bold rounded-xl hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm"
      >
        {loading ? <><i className="ri-loader-4-line animate-spin mr-2" />Signing in...</> : "Sign In"}
      </button>

      <p className="text-center text-sm text-gray-500">
        Don&apos;t have an account?{" "}
        <button type="button" onClick={onSwitch} className="text-orange-500 font-bold hover:underline cursor-pointer">
          Create Account
        </button>
      </p>
    </form>
  );
}
